import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BreadcrumbStructuredData from "@/components/BreadcrumbStructuredData";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";
import { generateLocalizedUrl } from "@/lib/slugUtils";
import FilloutButton from "@/components/FilloutButton";

interface IndustryPageProps {
  industry: 'real-estate' | 'ecommerce' | 'restaurants' | 'financial' | 'agencies' | 'construction';
}

const IndustryPage = ({ industry }: IndustryPageProps) => {
  const namespace = `industry-${industry}`;
  const { t, ready } = useTranslation([namespace, 'common']);
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  if (!ready) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground">{t('common:common.loading')}</p>
        </div>
      </div>
    );
  }

  const benefitsData = t(`${namespace}:benefits.items`, { returnObjects: true });
  const benefits = Array.isArray(benefitsData) ? benefitsData as Array<{
    title: string;
    description: string;
  }> : [];

  const useCasesData = t(`${namespace}:useCases.items`, { returnObjects: true });
  const useCases = Array.isArray(useCasesData) ? useCasesData as Array<{
    title: string;
    description: string;
  }> : [];

  return (
    <div className="min-h-screen">
      <SEOHead page={`industry-${industry}` as any} />
      <BreadcrumbStructuredData />
      <Header />

      {/* Breadcrumb Navigation */}
      <div className="container mx-auto px-4 py-4">
        <Breadcrumb />
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground" data-testid={`hero-${industry}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6 border-primary-foreground text-primary-foreground" data-testid={`badge-${industry}`}>
              {t(`${namespace}:hero.badge`)}
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid={`title-${industry}`}>
              {t(`${namespace}:hero.title`)}
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90" data-testid={`subtitle-${industry}`}>
              {t(`${namespace}:hero.subtitle`)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <FilloutButton 
                variant="secondary" 
                size="lg"
                data-testid={`button-cta-${industry}`}
              >
                {t(`${namespace}:hero.cta`)}
              </FilloutButton>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => navigate(generateLocalizedUrl('portfolio', currentLang))}
                className="border-primary-foreground bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                data-testid={`button-portfolio-${industry}`}
              >
                {t('common:buttons.viewMore')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-background" data-testid={`benefits-${industry}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4" data-testid={`benefits-title-${industry}`}>
                {t(`${namespace}:benefits.title`)}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid={`benefits-description-${industry}`}>
                {t(`${namespace}:benefits.description`)}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-2" data-testid={`benefit-card-${industry}-${index}`}>
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <CheckCircle className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle data-testid={`benefit-title-${industry}-${index}`}>
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground" data-testid={`benefit-description-${industry}-${index}`}>
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-muted/50" data-testid={`use-cases-${industry}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4" data-testid={`use-cases-title-${industry}`}>
                {t(`${namespace}:useCases.title`)}
              </h2>
              <p className="text-xl text-muted-foreground" data-testid={`use-cases-description-${industry}`}>
                {t(`${namespace}:useCases.description`)}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <Card key={index} className="bg-background" data-testid={`use-case-card-${industry}-${index}`}>
                  <CardHeader>
                    <CardTitle className="flex items-start gap-3" data-testid={`use-case-title-${industry}-${index}`}>
                      <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </span>
                      <span>{useCase.title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground" data-testid={`use-case-description-${industry}-${index}`}>
                      {useCase.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground" data-testid={`cta-${industry}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-testid={`cta-title-${industry}`}>
              {t(`${namespace}:cta.title`)}
            </h2>
            <p className="text-xl mb-8 opacity-90" data-testid={`cta-description-${industry}`}>
              {t(`${namespace}:cta.description`)}
            </p>
            <FilloutButton 
              variant="secondary" 
              size="lg"
              className="group"
              data-testid={`button-cta-bottom-${industry}`}
            >
              {t(`${namespace}:cta.button`)}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </FilloutButton>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustryPage;
